import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useAuth } from "../contexts/AuthContext";
import api, { API_ENDPOINTS } from "../config/api";
import {
  Settings,
  User,
  Mail,
  Shield,
  Bell,
  Moon,
  Sun,
  Globe,
  Palette,
  Key,
  Smartphone,
  ShieldCheck,
  AlertTriangle,
  Eye,
  EyeOff,
  Save,
  X,
  Wallet,
  Trash2,
  CheckCircle,
  Edit
} from "lucide-react";
import toast from "react-hot-toast";

const SettingsPage = () => {
  const { user, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState('account');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Update settings mutation
  const updateSettingsMutation = useMutation(
    (data) => {
      console.log('Sending settings update request:', data);
      return api.put(API_ENDPOINTS.USERS.UPDATE_SETTINGS, data);
    },
    {
      onSuccess: (response) => {
        console.log('Settings update response:', response.data);
        // Update user context with new preferences and notification email
        if (response.data) {
          updateUser({
            ...user,
            preferences: response.data.preferences || user.preferences,
            notificationEmail: response.data.notificationEmail !== undefined 
              ? response.data.notificationEmail 
              : user.notificationEmail
          });
        }
        queryClient.invalidateQueries(['user', user?._id]);
        queryClient.invalidateQueries(['auth', 'me']);
        toast.success('Settings updated successfully');
      },
      onError: (error) => {
        console.error('Settings update error:', error.response?.data || error.message);
        toast.error(error.response?.data?.error || 'Failed to update settings');
      },
    }
  );

  // Update profile mutation
  const updateProfileMutation = useMutation(
    (data) => api.put(API_ENDPOINTS.USERS.UPDATE_PROFILE, data),
    {
      onSuccess: (response) => {
        updateUser(response.data);
        toast.success('Profile updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update profile');
      },
    }
  );

  // Change password mutation
  const changePasswordMutation = useMutation(
    (data) => api.put(API_ENDPOINTS.USERS.CHANGE_PASSWORD, data),
    {
      onSuccess: () => {
        toast.success('Password changed successfully');
        setShowPasswordForm(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to change password');
      },
    }
  );

  if (!user) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Please log in to access settings
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            You need to be authenticated to access this page.
          </p>
        </div>
      </div>
    );
  }

  const handleUpdatePreferences = (preferences) => {
    updateSettingsMutation.mutate({ preferences });
  };

  const handleUpdateProfile = (profileData) => {
    updateProfileMutation.mutate({ profile: profileData });
  };

  const handleChangePassword = (passwordData) => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    changePasswordMutation.mutate({
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    });
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-heading mb-2">
          Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Settings Navigation */}
        <div className="lg:col-span-1">
          <div className="card p-4 sticky top-6">
            <nav className="space-y-2">
              <button
                onClick={() => setActiveSection('account')}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeSection === 'account'
                    ? 'bg-accent text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <User className="w-4 h-4 mr-3" />
                Account
              </button>
              <button
                onClick={() => setActiveSection('security')}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeSection === 'security'
                    ? 'bg-accent text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <Shield className="w-4 h-4 mr-3" />
                Security
              </button>
              <button
                onClick={() => setActiveSection('notifications')}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeSection === 'notifications'
                    ? 'bg-accent text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <Bell className="w-4 h-4 mr-3" />
                Notifications
              </button>
              <button
                onClick={() => setActiveSection('appearance')}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeSection === 'appearance'
                    ? 'bg-accent text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <Palette className="w-4 h-4 mr-3" />
                Appearance
              </button>
              <button
                onClick={() => setActiveSection('privacy')}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeSection === 'privacy'
                    ? 'bg-accent text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <Globe className="w-4 h-4 mr-3" />
                Privacy
              </button>
            </nav>
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          {activeSection === 'account' && (
            <AccountSection
              user={user}
              onUpdateProfile={handleUpdateProfile}
            />
          )}

          {activeSection === 'security' && (
            <SecuritySection
              user={user}
              showPasswordForm={showPasswordForm}
              setShowPasswordForm={setShowPasswordForm}
              showCurrentPassword={showCurrentPassword}
              setShowCurrentPassword={setShowCurrentPassword}
              showNewPassword={showNewPassword}
              setShowNewPassword={setShowNewPassword}
              showConfirmPassword={showConfirmPassword}
              setShowConfirmPassword={setShowConfirmPassword}
              onChangePassword={handleChangePassword}
              isLoading={changePasswordMutation.isLoading}
            />
          )}

          {activeSection === 'notifications' && (
            <NotificationsSection
              user={user}
              preferences={user.preferences}
              onUpdate={handleUpdatePreferences}
              isLoading={updateSettingsMutation.isLoading}
            />
          )}

          {activeSection === 'appearance' && (
            <AppearanceSection
              preferences={user.preferences}
              onUpdate={handleUpdatePreferences}
              isLoading={updateSettingsMutation.isLoading}
            />
          )}

          {activeSection === 'privacy' && (
            <PrivacySection
              user={user}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Account Section
const AccountSection = ({ user, onUpdateProfile }) => {
  const [formData, setFormData] = useState({
    firstName: user.profile?.firstName || '',
    lastName: user.profile?.lastName || '',
    email: user.email || '',
    phone: user.profile?.phone || '',
    location: user.profile?.location || '',
    bio: user.profile?.bio || '',
    website: user.profile?.socialLinks?.website || '',
    linkedin: user.profile?.socialLinks?.linkedin || '',
    github: user.profile?.socialLinks?.github || '',
    twitter: user.profile?.socialLinks?.twitter || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdateProfile({
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      location: formData.location,
      bio: formData.bio,
      socialLinks: {
        website: formData.website,
        linkedin: formData.linkedin,
        github: formData.github,
        twitter: formData.twitter,
      },
    });
  };

  return (
    <div className="card p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        Account Settings
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              First Name
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              className="input-field"
              placeholder="First Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Last Name
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              className="input-field"
              placeholder="Last Name"
            />
          </div>
        </div>

        {user.email && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              disabled
              className="input-field bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-60"
              placeholder="email@example.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Email cannot be changed
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input-field"
              placeholder="+1 234 567 8900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="input-field"
              placeholder="City, Country"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Bio
          </label>
          <textarea
            value={formData.bio}
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            className="input-field"
            rows={4}
            placeholder="Tell us about yourself..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
            Social Links
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2">
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="input-field"
                placeholder="https://yourwebsite.com"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2">
                LinkedIn
              </label>
              <input
                type="url"
                value={formData.linkedin}
                onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                className="input-field"
                placeholder="https://linkedin.com/in/username"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2">
                GitHub
              </label>
              <input
                type="url"
                value={formData.github}
                onChange={(e) => setFormData({ ...formData, github: e.target.value })}
                className="input-field"
                placeholder="https://github.com/username"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2">
                Twitter
              </label>
              <input
                type="url"
                value={formData.twitter}
                onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                className="input-field"
                placeholder="https://twitter.com/username"
              />
            </div>
          </div>
        </div>

        {user.walletAddress && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Wallet Address
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={user.walletAddress}
                className="input-field font-mono text-sm"
                readOnly
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(user.walletAddress);
                  toast.success('Wallet address copied to clipboard');
                }}
                className="btn-outline"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Your wallet address is connected and cannot be changed here
            </p>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button type="submit" className="btn-primary">
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

// Security Section
const SecuritySection = ({
  user,
  showPasswordForm,
  setShowPasswordForm,
  showCurrentPassword,
  setShowCurrentPassword,
  showNewPassword,
  setShowNewPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  onChangePassword,
  isLoading
}) => {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onChangePassword(passwordData);
  };

  if (!user.email) {
    return (
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Security Settings
        </h2>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Password management is not available for wallet-only accounts. Your wallet connection provides secure authentication.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        Security Settings
      </h2>

      <div className="space-y-6">
        {/* Password Change */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100">
                Password
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Change your account password
              </p>
            </div>
            {!showPasswordForm && (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="btn-outline"
              >
                Change Password
              </button>
            )}
          </div>

          {showPasswordForm && (
            <form onSubmit={handleSubmit} className="space-y-4 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    className="input-field pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="input-field pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Must be at least 6 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="input-field pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex space-x-3">
                <button type="submit" className="btn-primary" disabled={isLoading}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="btn-outline"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Account Security Info */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
            Account Security
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Email Verification
                  </p>
                  <p className="text-xs text-gray-500">
                    {user.email ? 'Verified' : 'Not set'}
                  </p>
                </div>
              </div>
              {user.email && <CheckCircle className="w-5 h-5 text-green-500" />}
            </div>

            {user.walletAddress && (
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Wallet className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Wallet Connected
                    </p>
                    <p className="text-xs text-gray-500 font-mono">
                      {user.walletAddress.slice(0, 6)}...{user.walletAddress.slice(-4)}
                    </p>
                  </div>
                </div>
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Notifications Section
const NotificationsSection = ({ user, preferences, onUpdate, isLoading }) => {
  const { updateUser } = useAuth();
  const [notificationSettings, setNotificationSettings] = useState({
    email: preferences?.notifications?.email ?? false,
    push: preferences?.notifications?.push ?? true,
    chat: preferences?.notifications?.chat ?? true,
  });
  const [notificationEmail, setNotificationEmail] = useState(user?.notificationEmail || '');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setNotificationEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSaveEmail = async () => {
    if (!notificationEmail.trim()) {
      setEmailError('Email address is required');
      return;
    }
    if (!validateEmail(notificationEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      const response = await api.put(API_ENDPOINTS.USERS.UPDATE_SETTINGS, {
        notificationEmail: notificationEmail.trim()
      });
      setIsEditingEmail(false);
      setEmailError('');
      // Update user context with new notification email
      updateUser({
        ...user,
        notificationEmail: response.data.notificationEmail
      });
      toast.success('Notification email updated successfully');
    } catch (error) {
      setEmailError(error.response?.data?.error || 'Failed to update notification email');
    }
  };

  const handleToggle = async (key) => {
    console.log('Toggle clicked:', key, 'Current state:', notificationSettings[key]);
    
    // If enabling email notifications, check if notification email is set
    if (key === 'email' && !notificationSettings.email && !user?.notificationEmail) {
      toast.error('Please set a notification email address first');
      setIsEditingEmail(true);
      return;
    }

    const updated = { ...notificationSettings, [key]: !notificationSettings[key] };
    console.log('Updating notification settings:', {
      key,
      newValue: updated[key],
      allSettings: updated
    });
    setNotificationSettings(updated);
    
    try {
      await onUpdate({
        notifications: updated,
      });
      console.log('Notification settings updated successfully');
    } catch (error) {
      console.error('Error updating notification settings:', error);
      // Revert on error
      setNotificationSettings(notificationSettings);
    }
  };

  return (
    <div className="card p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        Notification Settings
      </h2>

      <div className="space-y-6">
        {/* Notification Email Field */}
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Notification Email Address
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              This email will be used only for notifications. It can be different from your account email (if you have one).
            </p>
            {isEditingEmail || !user?.notificationEmail ? (
              <div className="space-y-2">
                <input
                  type="email"
                  value={notificationEmail}
                  onChange={handleEmailChange}
                  placeholder="Enter email address for notifications"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white ${
                    emailError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {emailError && (
                  <p className="text-sm text-red-500">{emailError}</p>
                )}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSaveEmail}
                    disabled={!notificationEmail.trim() || !!emailError || isLoading}
                    className="btn-primary text-sm"
                  >
                    {isLoading ? 'Saving...' : 'Save Email'}
                  </button>
                  {user?.notificationEmail && (
                    <button
                      onClick={() => {
                        setIsEditingEmail(false);
                        setNotificationEmail(user.notificationEmail);
                        setEmailError('');
                      }}
                      className="btn-outline text-sm"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                    {user.notificationEmail}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This email is used only for notifications
                  </p>
                </div>
                <button
                  onClick={() => setIsEditingEmail(true)}
                  className="btn-outline text-sm"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Update
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Email Notifications Toggle */}
        <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div>
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Email Notifications
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Receive notifications via email
            </p>
            {!user?.notificationEmail && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                ⚠️ Please set a notification email address first
              </p>
            )}
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={notificationSettings.email}
              onChange={() => handleToggle('email')}
              disabled={!user?.notificationEmail}
              className="sr-only peer disabled:opacity-50"
            />
            <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent ${!user?.notificationEmail ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
          </label>
        </div>

        <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div>
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Push Notifications
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Receive browser push notifications
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={notificationSettings.push}
              onChange={() => handleToggle('push')}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
          </label>
        </div>

        <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div>
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Chat Notifications
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Receive notifications for new messages
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={notificationSettings.chat}
              onChange={() => handleToggle('chat')}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
          </label>
        </div>
      </div>
    </div>
  );
};

// Appearance Section
const AppearanceSection = ({ preferences, onUpdate, isLoading }) => {
  const currentTheme = preferences?.theme || 'light';

  const handleThemeChange = (theme) => {
    onUpdate({ theme });
    // Apply theme to document
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  return (
    <div className="card p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        Appearance Settings
      </h2>

      <div className="space-y-6">
        <div>
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
            Theme
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleThemeChange('light')}
              className={`p-4 border-2 rounded-lg flex items-center space-x-3 transition-colors ${
                currentTheme === 'light'
                  ? 'border-accent bg-accent/10'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <Sun className="w-5 h-5" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-gray-100">Light</p>
                <p className="text-xs text-gray-500">Light mode</p>
              </div>
              {currentTheme === 'light' && (
                <CheckCircle className="w-5 h-5 text-accent ml-auto" />
              )}
            </button>

            <button
              onClick={() => handleThemeChange('dark')}
              className={`p-4 border-2 rounded-lg flex items-center space-x-3 transition-colors ${
                currentTheme === 'dark'
                  ? 'border-accent bg-accent/10'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <Moon className="w-5 h-5" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-gray-100">Dark</p>
                <p className="text-xs text-gray-500">Dark mode</p>
              </div>
              {currentTheme === 'dark' && (
                <CheckCircle className="w-5 h-5 text-accent ml-auto" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Privacy Section
const PrivacySection = ({ user }) => {
  return (
    <div className="card p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        Privacy Settings
      </h2>

      <div className="space-y-6">
        <div>
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
            Profile Visibility
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Your profile is currently public. Anyone can view your profile information.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Profile privacy controls are coming soon. For now, your profile is visible to all users.
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
            Account Information
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Username
                </p>
                <p className="text-xs text-gray-500">@{user.username}</p>
              </div>
              <span className="text-xs text-gray-500">Cannot be changed</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Member Since
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(user.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Account Status
                </p>
                <p className="text-xs text-gray-500">
                  {user.isActive ? 'Active' : 'Inactive'}
                </p>
              </div>
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
